import axios from 'axios';

/**
 * Central API client.
 *
 * Every request to the Gitribe API goes through this module. Having one entry
 * point means timeouts, base URL, error shaping and future concerns (tracing
 * headers, retries, auth) are configured once instead of at each call site.
 *
 * NOTE ON AUTHENTICATION
 * ----------------------
 * There is deliberately no API key here. Anything shipped to the browser is
 * readable by anyone who opens the bundle or devtools, so a key embedded in a
 * React app authenticates nothing — it is public by construction. The public
 * endpoints are instead protected server-side by an origin allowlist, per-IP
 * rate limiting, reCAPTCHA Enterprise scoring and strict input whitelisting.
 * See docs/SECURITY.md.
 */

const BASE_URL = process.env.REACT_APP_API_URL || '';
const DEFAULT_TIMEOUT_MS = 12000;

const client = axios.create({
  baseURL: BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Normalises every failure into one predictable shape so components never
 * have to interrogate an axios error object.
 *
 * @typedef {{ message: string, status: number|null, isNetworkError: boolean }} ApiError
 */
export class ApiError extends Error {
  constructor(message, status = null, isNetworkError = false) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.isNetworkError = isNetworkError;
  }
}

const toApiError = error => {
  if (error.response) {
    const { status, data } = error.response;
    return new ApiError(data?.error || `Request failed (${status})`, status, false);
  }
  if (error.request) {
    return new ApiError('Could not reach the server. Check your connection.', null, true);
  }
  return new ApiError(error.message || 'Unexpected error', null, false);
};

client.interceptors.response.use(
  response => response,
  error => Promise.reject(toApiError(error))
);

/* -------------------------------------------------------------------------- */
/*  Endpoints                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Submits the contact form.
 *
 * @param {object} payload
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, id: string }>}
 */
export const submitContactForm = async (payload, signal) => {
  const { data } = await client.post('/api/contacts', payload, { signal });
  return data;
};

/**
 * Latest blog posts for the header Insights panel.
 *
 * The API caches and normalises upstream WordPress data and never errors:
 * on an upstream outage it returns the last good result, or an empty list.
 *
 * @param {AbortSignal} [signal] Optional. `useLatestPosts` deliberately does
 *   not pass one — the request is shared between consumers, so aborting it
 *   from any single unmount would starve the others.
 * @returns {Promise<{ posts: Array, cached: boolean, stale?: boolean }>}
 */
export const fetchLatestPosts = async signal => {
  const { data } = await client.get('/api/blog/posts', { signal });
  return data;
};

export default client;
